import React, { useState, useEffect, useCallback } from 'react';
import MetaTags from "react-meta-tags";
import ApiClient from '../BaseUrl/ApiClient';
import { Link, useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { Button, Card, CardBody, CardHeader, Col, Container, Row, Input, Label, FormGroup } from 'reactstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTelegram, faInstagram } from '@fortawesome/free-brands-svg-icons';
import { faSearch, faLink, faCalendarAlt, faTimes, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { format, parseISO } from 'date-fns';
import './BusinessDetail.css';


const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

function BusinessList() {
  const [businesses, setBusinesses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dateLoading, setDateLoading] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [error, setError] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState('');
  const [subscriptionType, setSubscriptionType] = useState('');
  const history = useHistory();
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    totalDocs: 0,
    limit: 10,
  });

  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [passwordData, setPasswordData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});

  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState({
    search: false,
    date: false,
    status: false,
    type: false
  });


  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);



  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = typeof dateString === 'string' ? parseISO(dateString) : new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return format(date, 'MMM dd, yyyy');
    } catch (e) {
      return '-';
    }
  };

  const fetchBusinesses = async (page = 1, limit = pagination.limit, query = searchQuery, start = startDate, end = endDate, status = subscriptionStatus, type = subscriptionType) => {
    setIsLoading(true);
    setError(null);
    try {
      const params = { page, limit };
      if (query) params.query = query;

      if (start) {
        const utcStart = new Date(Date.UTC(
          start.getFullYear(),
          start.getMonth(),
          start.getDate()
        ));
        params.startDate = format(utcStart, 'yyyy-MM-dd');
      }

      if (end) {
        const utcEnd = new Date(Date.UTC(
          end.getFullYear(),
          end.getMonth(),
          end.getDate(),
          23, 59, 59, 999
        ));
        params.endDate = format(utcEnd, 'yyyy-MM-dd');
      }

      if (status) params.subscriptionStatus = status;
      if (type) params.subscriptionType = type;

      setActiveFilters({
        search: !!query,
        date: !!(start || end),
        status: !!status,
        type: !!type
      });

      const response = await ApiClient.get('/business/admin/list', { params });
      const { responseData } = response.data;
      setBusinesses(responseData.businesses);
      setPagination(responseData.pagination);

      if (responseData.businesses.length === 0 && (query || start || end || status || type)) {
        // toast.info("No businesses found with current filters");
      }
    } catch (error) {
      toast.error("Failed to fetch businesses.");
      if (error.response?.status === 401) {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("authUser");
        window.location.href = "/login";
      }
    } finally {
      setIsLoading(false);
      setDateLoading(false);
    }
  };

  const debouncedFetchBusinesses = useCallback(
    debounce((page, limit, query, start, end, status, type) => {
      fetchBusinesses(page, limit, query, start, end, status, type);
    }, 500),
    [pagination.limit]
  );

  useEffect(() => {
    fetchBusinesses();
  }, []);

  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    debouncedFetchBusinesses(1, pagination.limit, query, startDate, endDate, subscriptionStatus, subscriptionType);
  };

  const handlePageChange = (newPage) => {
    fetchBusinesses(newPage, pagination.limit, searchQuery, startDate, endDate, subscriptionStatus, subscriptionType);
  };

  const handleLimitChange = (e) => {
    const newLimit = Number(e.target.value);
    setPagination({ ...pagination, limit: newLimit });
    fetchBusinesses(1, newLimit, searchQuery, startDate, endDate, subscriptionStatus, subscriptionType);
  };

  const handleDateChange = (update) => {
    setDateRange(update);
    setDateLoading(true);

    setTimeout(() => {
      const [newStart, newEnd] = update;
      if (newStart && newEnd) {
        const start = new Date(newStart);
        const end = new Date(newEnd);

        if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
          const adjustedEndDate = new Date(end);
          adjustedEndDate.setDate(adjustedEndDate.getDate() - 1);
          debouncedFetchBusinesses(
            1,
            pagination.limit,
            searchQuery,
            start,
            adjustedEndDate,
            subscriptionStatus,
            subscriptionType
          );
        }
      } else if (!newStart && !newEnd) {
        fetchBusinesses(1, pagination.limit, searchQuery, null, null, subscriptionStatus, subscriptionType);
      }
      setDateLoading(false);
    }, 100);
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setDateRange([null, null]);
    setSubscriptionStatus('');
    setSubscriptionType('');
    setActiveFilters({
      search: false,
      date: false,
      status: false,
      type: false
    });
    fetchBusinesses(1, pagination.limit, '', null, null, '', '');
  };

  const clearSearchFilter = () => {
    setSearchQuery('');
    setActiveFilters(prev => ({ ...prev, search: false }));
    debouncedFetchBusinesses(1, pagination.limit, '', startDate, endDate, subscriptionStatus, subscriptionType);
  };

  const clearDateFilter = () => {
    setDateRange([null, null]);
    setActiveFilters(prev => ({ ...prev, date: false }));
    fetchBusinesses(1, pagination.limit, searchQuery, null, null, subscriptionStatus, subscriptionType);
  };

  const clearStatusFilter = () => {
    setSubscriptionStatus('');
    setActiveFilters(prev => ({ ...prev, status: false }));
    fetchBusinesses(1, pagination.limit, searchQuery, startDate, endDate, '', subscriptionType);
  };

  const clearTypeFilter = () => {
    setSubscriptionType('');
    setActiveFilters(prev => ({ ...prev, type: false }));
    fetchBusinesses(1, pagination.limit, searchQuery, startDate, endDate, subscriptionStatus, '');
  };

  const handleStatusChange = (e) => {
    const status = e.target.value;
    setSubscriptionStatus(status);
    fetchBusinesses(1, pagination.limit, searchQuery, startDate, endDate, status, subscriptionType);
  };

  const handleTypeChange = (e) => {
    const type = e.target.value;
    setSubscriptionType(type);
    fetchBusinesses(1, pagination.limit, searchQuery, startDate, endDate, subscriptionStatus, type);
  };

  const handleView = (businessId) => {
    history.push(`/business-detail/${businessId}`);
  };

  const updateSubscriptionType = async (id) => {
    history.push(`/update-subscription-type/${id}`);
  };


  const handleChangePasswordClick = (userId) => {
    setSelectedUserId(userId);
    setShowChangePasswordModal(true);
    setPasswordData({ password: '', confirmPassword: '' });
    setPasswordErrors({});
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user types
    if (passwordErrors[name]) {
      setPasswordErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const validatePasswordForm = () => {
    const errors = {};

    if (!passwordData.password) {
      errors.password = 'Password is required';
    } else if (passwordData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    if (!passwordData.confirmPassword) {
      errors.confirmPassword = 'Please confirm password';
    } else if (passwordData.password !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePasswordSubmit = async () => {
    if (!validatePasswordForm()) return;

    try {
      const response = await ApiClient.put(
        `/auth/admin/${selectedUserId}/changePassword`,
        passwordData
      );

      toast.success(response.data.responseMessage[0] || 'Password changed successfully');
      setShowChangePasswordModal(false);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("authUser");
        window.location.href = "/login";
      } else {
        toast.error(error.response?.data?.responseMessage[0] || 'Failed to change password');
      }
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <React.Fragment>
      <ToastContainer />
      <div className="page-content">
        <MetaTags>
          <title>Business List | Findagram</title>
        </MetaTags>
        <Container fluid>
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>

                <div className='d-flex justify-content-between align-items-center'>
                   <h2 className="card-titlebusiness mb-0">Businesses</h2>
              
                     <h2 className="card-title mb-0"> Total Businesses: {' '}
                    <span className="counter-value">
                      {pagination.totalDocs}
                    </span>
                  </h2>
                </div>
                  



                  {(activeFilters.search || activeFilters.date || activeFilters.status || activeFilters.type) && (
                    <Button
                      color="outline-danger"
                      size="sm"
                      onClick={clearAllFilters}
                    >
                      <FontAwesomeIcon icon={faTimes} className="me-1" />
                      Clear All Filters
                    </Button>
                  )}
                </CardHeader>

                <CardBody>
                  <div id="businessList">
                    <div className="mb-3 d-flex flex-wrap gap-2">
                      {activeFilters.search && (
                        <span className="badge bg-info bg-opacity-10 text-info d-flex align-items-center">
                          Search: {searchQuery}
                          <button
                            type="button"
                            className="btn-close btn-close-white ms-2 p-0"
                            aria-label="Close"
                            onClick={clearSearchFilter}
                            style={{ fontSize: '0.5rem', lineHeight: '1' }}
                          />
                        </span>
                      )}
                      {activeFilters.date && (
                        <span className="badge bg-info bg-opacity-10 text-info d-flex align-items-center">
                          Date: {startDate ? format(new Date(startDate), 'MMM dd, yyyy') : ''} - {endDate ? format(new Date(endDate), 'MMM dd, yyyy') : ''}
                          <button
                            type="button"
                            className="btn-close btn-close-white ms-2 p-0"
                            aria-label="Close"
                            onClick={clearDateFilter}
                            style={{ fontSize: '0.5rem', lineHeight: '1' }}
                          />
                        </span>
                      )}
                      {activeFilters.status && (
                        <span className="badge bg-info bg-opacity-10 text-info d-flex align-items-center">
                          Status: {subscriptionStatus}
                          <button
                            type="button"
                            className="btn-close btn-close-white ms-2 p-0"
                            aria-label="Close"
                            onClick={clearStatusFilter}
                            style={{ fontSize: '0.5rem', lineHeight: '1' }}
                          />
                        </span>
                      )}
                      {activeFilters.type && (
                        <span className="badge bg-info bg-opacity-10 text-info d-flex align-items-center">
                          Type: {subscriptionType}
                          <button
                            type="button"
                            className="btn-close btn-close-white ms-2 p-0"
                            aria-label="Close"
                            onClick={clearTypeFilter}
                            style={{ fontSize: '0.5rem', lineHeight: '1' }}
                          />
                        </span>
                      )}
                    </div>


                    <Row className="g-3 mb-4">
                      <Col md={3}>
                        <FormGroup>
                          <Label for="search" className="form-label">Search</Label>
                          <div className="search-box position-relative">
                            <Input
                              type="text"
                              id="search"
                              className="form-control ps-5"
                              placeholder="Search businesses..."
                              value={searchQuery}
                              onChange={handleSearchInputChange}
                              onKeyPress={(e) => e.key === 'Enter' && fetchBusinesses(1)}
                            />
                            <FontAwesomeIcon
                              icon={faSearch}
                              className="position-absolute"
                              style={{ top: '10px', left: '15px', color: '#6c757d' }}
                            />
                            <FontAwesomeIcon
                              icon={faTimes}
                              className={`clear-icon position-absolute ${!activeFilters.search ? 'd-none' : ''}`}
                              style={{ top: '12px', right: '10px', cursor: 'pointer' }}
                              onClick={clearSearchFilter}
                            />
                          </div>
                        </FormGroup>
                      </Col>

                      <Col md={3}>
                        <FormGroup>
                          <Label for="subscriptionStatus" className="form-label">Subscription Status</Label>
                          <div className="input-group">
                            <Input
                              type="select"
                              id="subscriptionStatus"
                              className="form-select"
                              value={subscriptionStatus}
                              onChange={handleStatusChange}
                            >
                              <option value="">All Statuses</option>
                              <option value="Subscribed">Subscribed</option>
                              <option value="Expired">Expired</option>
                              <option value="Not Subscribed">Not Subscribed</option>
                            </Input>
                            {activeFilters.status && (
                              <Button
                                color="outline-secondary"
                                onClick={clearStatusFilter}
                              >
                                <FontAwesomeIcon icon={faTimes} />
                              </Button>
                            )}
                          </div>
                        </FormGroup>
                      </Col>

                      <Col md={3}>
                        <FormGroup>
                          <Label for="subscriptionType" className="form-label">Subscription Type</Label>
                          <div className="input-group">
                            <Input
                              type="select"
                              id="subscriptionType"
                              className="form-select"
                              value={subscriptionType}
                              onChange={handleTypeChange}
                            >
                              <option value="">All Types</option>
                              <option value="standard">Standard</option>
                              <option value="featured">Featured</option>
                              <option value="sponsored">Sponsored</option>
                            </Input>
                            {activeFilters.type && (
                              <Button
                                color="outline-secondary"
                                onClick={clearTypeFilter}
                              >
                                <FontAwesomeIcon icon={faTimes} />
                              </Button>
                            )}
                          </div>
                        </FormGroup>
                      </Col>

                      <Col md={3}>
                        <FormGroup>
                          <Label className="form-label">Subscription Expiry Date</Label>
                          <div className="d-flex align-items-center gap-2">
                            <div className="position-relative flex-grow-1">
                              <FontAwesomeIcon
                                icon={faCalendarAlt}
                                className="position-absolute"
                                style={{ top: '12px', left: '10px', zIndex: 1, color: '#6c757d' }}
                              />
                              {dateLoading && (
                                <div
                                  className="spinner-border spinner-border-sm position-absolute"
                                  style={{ right: '10px', top: '10px', zIndex: 1 }}
                                />
                              )}
                              <DatePicker
                                selectsRange={true}
                                startDate={startDate ? new Date(startDate) : null}
                                endDate={endDate ? new Date(endDate) : null}
                                onChange={handleDateChange}
                                placeholderText="Expiry Date Range"
                                className="form-control"
                                dateFormat="yyyy-MM-dd"
                                isClearable={true}
                              />
                            </div>
                            {activeFilters.date && (
                              <Button
                                color="outline-secondary"
                                onClick={clearDateFilter}
                                className="ms-1"
                                title="Clear date"
                              >
                                <FontAwesomeIcon icon={faTimes} />
                              </Button>
                            )}
                          </div>
                        </FormGroup>
                      </Col>

                    </Row>

                    {/* Business Table */}
                    <div className="table-responsive table-card mt-3 mb-1">
                      <div className="table-scroll-x">
                        <table className="table align-middle table-nowrap" id="businessTable">
                          <thead className="table-light">
                            <tr>
                              <th>User Name</th>
                              <th>Email</th>
                              <th>Logo</th>
                              <th>Business Name</th>
                              <th>Subscription Start</th>
                              <th>Subscription Expiry</th>
                              <th>Payment Method</th>
                              <th>Status</th>
                              <th>Type</th>
                              <th>Location</th>
                              <th>Phone</th>
                              <th>Working Hours</th>
                              <th>Telegram</th>
                              <th>Instagram</th>
                              <th>Website</th>
                              <th>Rating</th>
                              <th>Reviews</th>

                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody className="list form-check-all">
                            {businesses.map((business, index) => (
                              <tr key={index}>
                                <td className="user_name">
                                  {business.user?.firstName} {business.user?.lastName}
                                </td>
                                <td className="user_email">{business.user?.email || 'No Email'}</td>
                                <td className="logo">
                                  {business.logoImage?.url ? (
                                    <img
                                      src={`${process.env.REACT_APP_LOGO_BASE_URL}/${business.logoImage.url}`}
                                      alt="Business logo"
                                      className="img-thumbnail"
                                      style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                    />
                                  ) : (
                                    <span className="text-muted">No logo</span>
                                  )}
                                </td>
                                <td className="business_name">{business.businessName || 'No Business Name'}</td>
                                <td className="subscription_start">
                                  {business.subscription?.subscriptionStartDate ? formatDateForDisplay(business.subscription.subscriptionStartDate) : '-'}
                                </td>
                                <td className="subscription_expiry">
                                  {business.subscription?.subscriptionExpiryDate ? formatDateForDisplay(business.subscription.subscriptionExpiryDate) : '-'}
                                </td>
                                <td className="subscription_method">
                                  {business.subscription?.subscriptionMethod === 'stripe'
                                    ? 'Credit Card'
                                    : business.subscription?.subscriptionMethod || '-'}
                                </td>
                                <td className="status">
                                  {business.subscription?.subscriptionStatus || 'Not Subscribed'}
                                </td>
                                <td className="type">
                                  {business.subscriptionType || '-'}
                                </td>
                                <td className="location">
                                  {business.businessLocation?.city ? `${business.businessLocation.city}, ${business.businessLocation.state || ''} ${business.businessLocation.postalCode || ''}`.trim() : 'No Location'}
                                </td>
                                <td className="phone">{business.phone || 'No Phone'}</td>
                                <td className="working_hours">
                                  {business.workingHours || 'Not Specified'}
                                </td>
                                <td className="telegram">
                                  {business.telegramLink ? (
                                    <a href={business.telegramLink} target="_blank" rel="noopener noreferrer">
                                      <FontAwesomeIcon icon={faTelegram} /> Telegram
                                    </a>
                                  ) : 'No Telegram'}
                                </td>
                                <td className="instagram">
                                  {business.instagramId ? (
                                    <a href={business.instagramId} target="_blank" rel="noopener noreferrer">
                                      <FontAwesomeIcon icon={faInstagram} /> Instagram
                                    </a>
                                  ) : 'No Instagram'}
                                </td>
                                <td className="websiteLink">
                                  {business.websiteLink ? (
                                    <a href={business.websiteLink} target="_blank" rel="noopener noreferrer">
                                      <FontAwesomeIcon icon={faLink} /> Website
                                    </a>
                                  ) : 'No Website'}
                                </td>
                                <td className="rating">
                                  {business?.averageRating > 0 ? business?.averageRating : '-'}
                                </td>
                                <td className="reviews">
                                  {business.totalReviews || '0'}
                                </td>

                                <td>
                                  <div className="d-flex gap-2">
                                    <Button
                                      color="success"
                                      size="sm"
                                      onClick={() => handleView(business._id)}
                                    >
                                      View
                                    </Button>
                                    <Button
                                      color="warning"
                                      size="sm"
                                      onClick={() => updateSubscriptionType(business._id)}
                                    >
                                      Update Subscription
                                    </Button>
                                    <Button
                                      color="danger"
                                      size="sm"
                                      onClick={() => handleChangePasswordClick(business.user?._id)}
                                    >
                                      Change Password
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {isLoading && businesses.length === 0 ? (
                        <div className="text-center py-4">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <h5 className="mt-2">Loading Businesses...</h5>
                        </div>
                      ) : businesses.length === 0 ? (
                        <div className="text-center py-4">
                          <lord-icon
                            src="https://cdn.lordicon.com/msoeawqm.json"
                            trigger="loop"
                            colors="primary:#121331,secondary:#08a88a"
                            style={{ width: '75px', height: '75px' }}
                          />
                          <h5 className="mt-2">No Businesses Found</h5>
                          <p className="text-muted">
                            {activeFilters.search || activeFilters.date || activeFilters.status || activeFilters.type
                              ? "Try adjusting your filters"
                              : "No businesses available"}
                          </p>
                        </div>
                      ) : null}
                    </div>

                    {/* Pagination */}
                    <div className="d-flex justify-content-end align-items-center mt-3">
                      <div className="pagination-wrap hstack gap-2">
                        <Button
                          color="light"
                          disabled={pagination.page === 1}
                          onClick={() => handlePageChange(pagination.page - 1)}
                        >
                          Previous
                        </Button>

                        <div className="d-flex gap-1">
                          {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                            let pageNum;
                            if (pagination.totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (pagination.page <= 3) {
                              pageNum = i + 1;
                            } else if (pagination.page >= pagination.totalPages - 2) {
                              pageNum = pagination.totalPages - 4 + i;
                            } else {
                              pageNum = pagination.page - 2 + i;
                            }

                            return (
                              <Button
                                key={pageNum}
                                color={pagination.page === pageNum ? 'primary' : 'light'}
                                onClick={() => handlePageChange(pageNum)}
                              >
                                {pageNum}
                              </Button>
                            );
                          })}
                        </div>

                        <Button
                          color="light"
                          disabled={pagination.page === pagination.totalPages}
                          onClick={() => handlePageChange(pagination.page + 1)}
                        >
                          Next
                        </Button>

                        <div className="form-group mb-0">
                          <select
                            className="form-select"
                            value={pagination.limit}
                            onChange={handleLimitChange}
                            style={{ width: '80px' }}
                          >
                            <option value="10">10</option>
                            <option value="20">20</option>
                            <option value="50">50</option>
                          </select>
                        </div>
                      </div>
                    </div>

                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
 {/* Change Password Modal */}
<div className={`modal fade ${showChangePasswordModal ? 'show d-block' : ''}`} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
  <div className="modal-dialog modal-dialog-centered">
    <div className="modal-content">
      <div className="modal-header">
        <h5 className="modal-title">Change Password</h5>
        <button
          type="button"
          className="btn-close"
          onClick={() => setShowChangePasswordModal(false)}
        ></button>
      </div>
<div className="modal-body">
  <FormGroup>
    <Label for="password">New Password</Label>
    <div className="position-relative">
      <Input
        type={showPassword ? "text" : "password"}
        id="password"
        name="password"
        value={passwordData.password}
        onChange={handlePasswordChange}
        invalid={!!passwordErrors.password}
        style={{
          paddingRight: passwordErrors.password ? "40px" : "30px",
        }}
      />
      <FontAwesomeIcon
        icon={showPassword ? faEyeSlash : faEye}
        className="position-absolute top-50 translate-middle-y"
        style={{
          right: passwordErrors.password ? "30px" : "10px", // Adjust right when validation shows
          paddingBottom: passwordErrors.password ? "20px" : "0", // Apply padding-bottom only when validation shows
          transform: "translateY(-50%)",
          cursor: "pointer",
        }}
        onClick={togglePasswordVisibility}
      />
      {passwordErrors.password && (
        <div className="invalid-feedback d-block">
          {passwordErrors.password}
        </div>
      )}
    </div>
  </FormGroup>

  <FormGroup>
    <Label for="confirmPassword">Confirm Password</Label>
    <div className="position-relative">
      <Input
        type={showConfirmPassword ? "text" : "password"}
        id="confirmPassword"
        name="confirmPassword"
        value={passwordData.confirmPassword}
        onChange={handlePasswordChange}
        invalid={!!passwordErrors.confirmPassword}
        style={{
          paddingRight: passwordErrors.confirmPassword ? "40px" : "30px",
        }}
      />
      <FontAwesomeIcon
        icon={showConfirmPassword ? faEyeSlash : faEye}
        className="position-absolute top-50 translate-middle-y"
        style={{
          right: passwordErrors.confirmPassword ? "30px" : "10px", // Adjust right when validation shows
          paddingBottom: passwordErrors.confirmPassword ? "20px" : "0", // Apply padding-bottom only when validation shows
          transform: "translateY(-50%)",
          cursor: "pointer",
        }}
        onClick={toggleConfirmPasswordVisibility}
      />
      {passwordErrors.confirmPassword && (
        <div className="invalid-feedback d-block">
          {passwordErrors.confirmPassword}
        </div>
      )}
    </div>
  </FormGroup>
</div>
      <div className="modal-footer">
        <Button
          color="secondary"
          onClick={() => setShowChangePasswordModal(false)}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handlePasswordSubmit}
        >
          Update Password
        </Button>
      </div>
    </div>
  </div>
</div>
    </React.Fragment>
  );
}

export default BusinessList;